import { z } from 'zod';
import { defineRoute } from '../core/contract';
import { GAME_EVENT_REJECT_CODES } from '../core/errors';
import { eventBatchResZ, MAX_EVENT_BATCH } from '../core/event-batch';
import { IdZ, OkZ, qInt, TsZ } from '../schemas/common';
import { GameEventZ, StoredGameEventZ } from '../schemas/event';
import {
  GameDepthZ,
  GameDetailZ,
  GameModeZ,
  GamePlayerZ,
  GameStatusZ,
  GameSummaryZ,
  GameZ,
  KeeperStateZ,
} from '../schemas/game';
import { RulesZ } from '../schemas/rules';

/**
 * Owner: games. Event-sourced scoring (R2 to R7). There is no finish route and no void
 * route: `end` and `void` are ordinary events in the batch. The creating device holds the
 * book at epoch 1.
 */

export const GameCreateZ = z.object({
  id: IdZ,
  mode: GameModeZ,
  depth: GameDepthZ,
  /** Defaults to DEPTH_PRESET[depth]. */
  coverage: z.number().int().min(0).max(2047).optional(),
  rules: RulesZ,
  honor: z.boolean().default(false),
  players: z.array(GamePlayerZ).min(2).max(20),
  playedAt: TsZ,
  clientTs: TsZ,
  tournamentId: IdZ.nullable().optional(),
  fixtureId: IdZ.nullable().optional(),
  drillSessionId: IdZ.nullable().optional(),
  /** undefined: auto (sessions.public.defaultSessionFor at clientTs). null: detached. */
  sessionId: IdZ.nullable().optional(),
  /**
   * The member who holds the book at epoch 1. Default (undefined or null): the creator. "Next game"
   * passes the sitter, so the book is with them the moment the game exists. Unknown or disabled
   * members fall back to the creator, so an offline create never fails on this.
   */
  keeperMemberId: IdZ.nullable().optional(),
});
export type GameCreate = z.input<typeof GameCreateZ>;

export const EventBatchReqZ = z.object({
  events: z.array(GameEventZ).min(1).max(MAX_EVENT_BATCH),
});
export const GameEventBatchResZ = eventBatchResZ(GAME_EVENT_REJECT_CODES);
export type GameEventBatchRes = z.infer<typeof GameEventBatchResZ>;

export const listGames = defineRoute({
  name: 'games.list',
  method: 'GET',
  path: '/api/games',
  auth: 'mem',
  query: z.object({
    status: GameStatusZ.optional(),
    playerId: IdZ.optional(),
    mode: GameModeZ.optional(),
    sessionId: IdZ.optional(),
    /** playedAt cursor (exclusive). */
    before: qInt(0, Number.MAX_SAFE_INTEGER).optional(),
    limit: qInt(1, 100).optional(),
  }),
  res: z.object({ items: z.array(GameSummaryZ), nextBefore: TsZ.nullable() }),
});

export const createGame = defineRoute({
  name: 'games.create',
  method: 'POST',
  path: '/api/games',
  auth: 'memw',
  body: GameCreateZ,
  res: GameDetailZ,
  status: 201,
  idempotent: true,
  errors: ['VALIDATION', 'NOT_FOUND'],
});

export const getGame = defineRoute({
  name: 'games.get',
  method: 'GET',
  path: '/api/games/:id',
  auth: 'mem',
  res: GameDetailZ,
  errors: ['NOT_FOUND'],
});

export const postEvents = defineRoute({
  name: 'games.postEvents',
  method: 'POST',
  path: '/api/games/:id/events',
  auth: 'sk',
  body: EventBatchReqZ,
  res: GameEventBatchResZ,
  maxBodyBytes: 256 * 1024,
  errors: ['NOT_FOUND'],
  summary: 'Up to 200 events. Insert ON CONFLICT(id) DO NOTHING. Per-event codes in `rejected`.',
});

export const pullEvents = defineRoute({
  name: 'games.pullEvents',
  method: 'GET',
  path: '/api/games/:id/events',
  auth: 'mem',
  query: z.object({ after: qInt(0, Number.MAX_SAFE_INTEGER).optional() }),
  res: z.object({
    events: z.array(StoredGameEventZ),
    serverSeq: z.number().int().nonnegative(),
    serverNow: TsZ,
  }),
});

export const keeper = defineRoute({
  name: 'games.keeper',
  method: 'POST',
  path: '/api/games/:id/keeper',
  auth: 'mem',
  body: z.object({
    action: z.enum(['take', 'give']),
    /** For give: the member who gets the book. */
    toMemberId: IdZ.optional(),
  }),
  res: KeeperStateZ,
  errors: ['NOT_FOUND', 'GAME_LOCKED', 'FORBIDDEN'],
  summary: 'Take bumps the epoch (R7). Old-epoch events with clientTs before the take still count.',
});

export const patchGame = defineRoute({
  name: 'games.patch',
  method: 'PATCH',
  path: '/api/games/:id',
  auth: 'sk',
  body: z
    .object({
      rules: RulesZ,
      depth: GameDepthZ,
      coverage: z.number().int().min(0).max(2047),
    })
    .partial(),
  res: GameDetailZ,
  errors: ['NOT_FOUND', 'GAME_LOCKED', 'FORBIDDEN'],
  summary: 'Fix rules or depth after the fact. Keeper within 24 h of the end, owner any time. Bumps revision, audited.',
});

export const deleteGame = defineRoute({
  name: 'games.delete',
  method: 'DELETE',
  path: '/api/games/:id',
  auth: 'own',
  res: OkZ,
  summary: 'Soft delete (deleted_at). Drops the game from every derived table.',
});

export const restoreGame = defineRoute({
  name: 'games.restore',
  method: 'POST',
  path: '/api/games/:id/restore',
  auth: 'own',
  res: GameDetailZ,
});

/** Spectator stream messages (R14). `id:` of each message is the serverSeq. */
export const LiveGameMessageZ = z.discriminatedUnion('event', [
  z.object({
    event: z.literal('game.snapshot'),
    data: z.object({
      game: GameZ,
      players: z.array(GamePlayerZ),
      events: z.array(StoredGameEventZ),
      serverSeq: z.number().int().nonnegative(),
    }),
  }),
  z.object({ event: z.literal('game.event'), data: z.object({ events: z.array(StoredGameEventZ) }) }),
  z.object({
    event: z.literal('game.status'),
    data: z.object({
      status: GameStatusZ,
      keeperMemberId: IdZ.nullable(),
      keeperEpoch: z.number().int(),
      revision: z.number().int(),
    }),
  }),
  z.object({
    event: z.literal('game.final'),
    data: z.object({ status: GameStatusZ, scoreA: z.number().int(), scoreB: z.number().int() }),
  }),
  z.object({ event: z.literal('bye'), data: z.object({}) }),
]);
export type LiveGameMessage = z.infer<typeof LiveGameMessageZ>;

export const liveGame = defineRoute({
  name: 'games.live',
  method: 'GET',
  path: '/api/live/games/:id',
  auth: 'mem',
  kind: 'sse',
  res: LiveGameMessageZ,
  summary: 'SSE, 15 s ping, Last-Event-ID = serverSeq. Never routed through the service worker.',
});
